const DESKTOP_MEDIA = '(min-width: 1000px)';
const cardProductDesktopMedia = window.matchMedia(DESKTOP_MEDIA);

/**
 * @param {HTMLElement} productSwatches
 * @returns {{ desktopLimit: number, mobileLimit: number, isDesktop: boolean }}
 */
function getSwatchLimitState(productSwatches) {
  const isDesktop = window.matchMedia(DESKTOP_MEDIA).matches;
  const desktopLimit = parseInt(productSwatches.dataset.desktopLimit || '5', 10);
  const mobileLimit = parseInt(productSwatches.dataset.mobileLimit || '5', 10);

  return { desktopLimit, mobileLimit, isDesktop };
}

/**
 * @param {HTMLElement} productSwatches
 */
function applySwatchVisibilityClasses(productSwatches) {
  const { desktopLimit, mobileLimit } = getSwatchLimitState(productSwatches);

  productSwatches.querySelectorAll('li.variant-option__swatch').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;

    const index = parseInt(item.dataset.swatchIndex || '0', 10);
    item.classList.toggle('product-card-swatch--hidden-desktop', index > desktopLimit);
    item.classList.toggle('product-card-swatch--hidden-mobile', index > mobileLimit);
  });
}

/**
 * @param {HTMLElement} productSwatches
 */
/**
 * @param {HTMLElement} overflowList
 * @param {boolean} showMore
 */
function setOverflowListMoreSlotVisible(overflowList, showMore) {
  const shadowRoot = overflowList.shadowRoot;
  if (!shadowRoot) return;

  const moreSlot = shadowRoot.querySelector('slot[name="more"]');
  if (moreSlot instanceof HTMLElement) {
    moreSlot.hidden = !showMore;
  }
}

/**
 * @param {HTMLElement} productSwatches
 * @param {number} activeOverflow
 */
function syncCmsOverflowMoreControl(productSwatches, activeOverflow) {
  const overflowItem = productSwatches.querySelector('.product-card-swatch__overflow-count');
  const overflowList = productSwatches.querySelector('overflow-list');
  const showMore = activeOverflow > 0;

  if (overflowItem instanceof HTMLElement) {
    overflowItem.hidden = !showMore;
    if (showMore) {
      overflowItem.removeAttribute('hidden');
    }
  }

  if (overflowList instanceof HTMLElement) {
    overflowList.style.setProperty('--overflow-count', String(activeOverflow));
    overflowList.style.setProperty('--limit-overflow-count', String(activeOverflow));
    setOverflowListMoreSlotVisible(overflowList, showMore);
  }
}

function updateOverflowIndicator(productSwatches) {
  const { desktopLimit, mobileLimit, isDesktop } = getSwatchLimitState(productSwatches);
  const swatchCount = productSwatches.querySelectorAll('li.variant-option__swatch').length;
  const desktopOverflow = Math.max(0, swatchCount - desktopLimit);
  const mobileOverflow = Math.max(0, swatchCount - mobileLimit);
  const activeOverflow = isDesktop ? desktopOverflow : mobileOverflow;

  productSwatches.toggleAttribute('data-show-desktop-overflow', desktopOverflow > 0);
  productSwatches.toggleAttribute('data-show-mobile-overflow', mobileOverflow > 0);
  productSwatches.dataset.desktopOverflow = String(desktopOverflow);
  productSwatches.dataset.mobileOverflow = String(mobileOverflow);

  productSwatches.style.setProperty('--limit-overflow-count-desktop', String(desktopOverflow));
  productSwatches.style.setProperty('--limit-overflow-count-mobile', String(mobileOverflow));
  productSwatches.style.setProperty('--limit-overflow-count', String(activeOverflow));

  syncCmsOverflowMoreControl(productSwatches, activeOverflow);
}

/**
 * @param {HTMLElement} productSwatches
 */
function watchOverflowListReady(productSwatches) {
  if (productSwatches.dataset.overflowWatchInit === 'true') return;
  productSwatches.dataset.overflowWatchInit = 'true';

  const overflowList = productSwatches.querySelector('overflow-list');
  if (!(overflowList instanceof HTMLElement)) return;

  const reapply = () => {
    if (productSwatches.dataset.limitColorVariants === 'true') {
      applyProductSwatchLimits(productSwatches, { skipReflow: true });
    }
  };

  overflowList.addEventListener('reflow', reapply);

  if (overflowList.hasAttribute('defer')) {
    const deferObserver = new MutationObserver(() => {
      if (!overflowList.hasAttribute('defer')) {
        reapply();
        deferObserver.disconnect();
      }
    });
    deferObserver.observe(overflowList, { attributes: true, attributeFilter: ['defer'] });
  }

  requestAnimationFrame(reapply);
  setTimeout(reapply, 100);
}

/**
 * @param {HTMLElement} productSwatches
 */
/**
 * @param {HTMLElement} productSwatches
 * @param {{ skipReflow?: boolean }} [options]
 */
function applyProductSwatchLimits(productSwatches, options = {}) {
  if (!(productSwatches instanceof HTMLElement)) return;
  if (productSwatches.dataset.limitColorVariants !== 'true') return;
  if (productSwatches.dataset.limitExpanded === 'true') return;

  applySwatchVisibilityClasses(productSwatches);
  updateOverflowIndicator(productSwatches);

  if (options.skipReflow) return;

  const overflowList = productSwatches.querySelector('overflow-list');
  if (overflowList instanceof HTMLElement) {
    overflowList.dispatchEvent(new CustomEvent('reflow', { bubbles: true, detail: {} }));
  }
}

/**
 * @param {ParentNode} [root]
 */
function initProductSwatchLimits(root = document) {
  root.querySelectorAll('product-swatches[data-limit-color-variants="true"]').forEach((element) => {
    if (element instanceof HTMLElement) {
      watchOverflowListReady(element);
      applyProductSwatchLimits(element);
    }
  });
}

function handleSwatchLimitExpand(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const productSwatches = target.closest('product-swatches[data-limit-color-variants="true"]');
  if (!(productSwatches instanceof HTMLElement)) return;

  const button = target.closest('.product-card-swatch__overflow-button');
  if (!button) return;
  if (productSwatches.dataset.limitExpanded === 'true') return;

  productSwatches.dataset.limitExpanded = 'true';

  productSwatches.querySelectorAll('li.variant-option__swatch').forEach((item) => {
    if (item instanceof HTMLElement) {
      item.classList.remove('product-card-swatch--hidden-desktop', 'product-card-swatch--hidden-mobile');
    }
  });

  const overflowItem = productSwatches.querySelector('.product-card-swatch__overflow-count');
  if (overflowItem instanceof HTMLElement) {
    overflowItem.hidden = true;
  }

  const overflowList = productSwatches.querySelector('overflow-list');
  if (overflowList instanceof HTMLElement && typeof overflowList.showAll === 'function') {
    overflowList.showAll();
  }
}

document.addEventListener('click', handleSwatchLimitExpand);

/**
 * @param {HTMLElement} productSwatches
 */
function updateSelectedSwatchName(productSwatches) {
  if (productSwatches.dataset.showSelectedSwatchName !== 'true') return;

  const label = productSwatches.querySelector('.product-card__selected-swatch-name');
  if (!(label instanceof HTMLElement)) return;

  // Set cards (product-card-bundle-swatches) have no radios: the server already wrote the set's colour
  // (bundle_product tag), so leave it alone instead of blanking it for want of a checked swatch.
  if (!productSwatches.querySelector('swatches-variant-picker-component')) return;

  const checked = productSwatches.querySelector(
    'swatches-variant-picker-component input[type="radio"][name*="-swatch"]:checked'
  );

  if (checked instanceof HTMLInputElement) {
    const name = checked.getAttribute('aria-label')?.trim() || checked.value?.trim() || '';
    if (name) {
      // Option values are stored in capitals; CSS text-transform: capitalize title-cases the lower-cased name.
      label.textContent = name.toLowerCase();
      label.hidden = false;
      return;
    }
  }

  label.hidden = true;
  label.textContent = '';
}

/**
 * @param {HTMLElement} productSwatches
 */
function initSelectedSwatchNameListeners(productSwatches) {
  if (productSwatches.dataset.showSelectedSwatchName !== 'true') return;

  if (productSwatches.dataset.swatchNameInit === 'true') {
    updateSelectedSwatchName(productSwatches);
    return;
  }

  productSwatches.dataset.swatchNameInit = 'true';

  productSwatches.addEventListener('change', (event) => {
    const target = event.target;
    if (
      target instanceof HTMLInputElement &&
      target.type === 'radio' &&
      target.name?.includes('-swatch')
    ) {
      updateSelectedSwatchName(productSwatches);
    }
  });

  updateSelectedSwatchName(productSwatches);
}

/**
 * @param {ParentNode} [root]
 */
function initSelectedSwatchNames(root = document) {
  root.querySelectorAll('product-swatches[data-show-selected-swatch-name="true"]').forEach((element) => {
    if (element instanceof HTMLElement) {
      initSelectedSwatchNameListeners(element);
    }
  });
}

function scheduleSwatchLimitInit(root = document) {
  initProductSwatchLimits(root);
  initSelectedSwatchNames(root);
  requestAnimationFrame(() => {
    initProductSwatchLimits(root);
    initSelectedSwatchNames(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  scheduleSwatchLimitInit();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    scheduleSwatchLimitInit(event.target);
  }
});

document.addEventListener('shopify:section:reorder', (event) => {
  if (event.target instanceof HTMLElement) {
    scheduleSwatchLimitInit(event.target);
  }
});

cardProductDesktopMedia.addEventListener('change', () => {
  document.querySelectorAll('product-swatches[data-limit-color-variants="true"]').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.limitExpanded !== 'true') {
      applyProductSwatchLimits(element);
    }
  });
});

const swatchLimitObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      if (node.matches('product-swatches[data-limit-color-variants="true"]')) {
        applyProductSwatchLimits(node);
      }

      if (node.matches('product-swatches[data-show-selected-swatch-name="true"]')) {
        initSelectedSwatchNameListeners(node);
      }

      if (node.matches('product-swatches[data-limit-color-variants="true"]')) {
        return;
      }

      initProductSwatchLimits(node);
      initSelectedSwatchNames(node);
    });
  }
});

swatchLimitObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});

/**
 * Card rows line up: product titles wrap to 1-4 lines, which pushed swatches,
 * colour name and price/Compare to different heights across a row. Titles in
 * the same visual row (same section, same top) get the height of the tallest.
 * CSS reserves two lines (custom.css) so short titles do not jump before this runs.
 *
 * Performance (2026-09-28): each pass used to clear every title's min-height and then measure, which
 * made the browser lay the page out two extra times per pass (about 30 to 180 ms a pass on a phone).
 * A title's own height is now read from its content (the bottom of its last child box), so nothing is
 * cleared first, the page is laid out once, and min-height is only written where it changes. The result
 * is the same height as before; titles whose content cannot be measured that way use the old method.
 */
const CARD_TITLE_SELECTOR = 'product-card .product-title-content';
let cardRowFrame = 0;

/**
 * Bottom edge of an element's content, from its child boxes (children with display: contents are
 * looked through). Returns null when no child has a box.
 * @param {Element} element
 * @returns {number | null}
 */
function contentBottom(element) {
  let bottom = null;
  for (const child of element.children) {
    let childBottom = null;
    if (child.getClientRects().length) {
      childBottom = child.getBoundingClientRect().bottom + (parseFloat(getComputedStyle(child).marginBottom) || 0);
    } else if (getComputedStyle(child).display === 'contents') {
      childBottom = contentBottom(child);
    }
    if (childBottom !== null && (bottom === null || childBottom > bottom)) bottom = childBottom;
  }
  return bottom;
}

/**
 * Height the title needs for its own content, whatever min-height it has now.
 * @param {HTMLElement} title
 * @param {DOMRect} rect - the title's current box
 * @returns {number | null}
 */
function naturalTitleHeight(title, rect) {
  const bottom = contentBottom(title);
  if (bottom === null) return null;
  const style = getComputedStyle(title);
  const height =
    bottom - rect.top + (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  // Layout positions are multiples of 1/64 px: snap so the subtraction cannot round up a whole pixel
  return Math.round(height * 64) / 64;
}

function alignCardTitleRows() {
  cancelAnimationFrame(cardRowFrame);
  cardRowFrame = requestAnimationFrame(() => {
    // CARD_TITLE_SELECTOR pass: reads first (one layout), writes after
    /** @type {HTMLElement[]} */
    const titles = [...document.querySelectorAll(CARD_TITLE_SELECTOR)].filter(
      (title) => title instanceof HTMLElement && title.getClientRects().length > 0
    );

    /** @type {Map<string, { title: HTMLElement, height: number | null }[]>} */
    const rows = new Map();
    /** @type {{ title: HTMLElement, height: number | null }[]} */
    const measured = [];
    for (const title of titles) {
      const card = title.closest('product-card');
      if (!card) continue;
      const section = card.closest('.shopify-section')?.id || '';
      const key = `${section}:${Math.round(card.getBoundingClientRect().top + window.scrollY)}`;
      const entry = { title, height: naturalTitleHeight(title, title.getBoundingClientRect()) };
      measured.push(entry);
      const row = rows.get(key);
      if (row) row.push(entry);
      else rows.set(key, [entry]);
    }

    // Titles that could not be measured from their content: measure them the old way (clear, then read)
    const unmeasured = measured.filter((entry) => entry.height === null);
    if (unmeasured.length) {
      unmeasured.forEach(({ title }) => (title.style.minHeight = ''));
      unmeasured.forEach((entry) => (entry.height = entry.title.getBoundingClientRect().height));
    }

    /** @type {Map<HTMLElement, string>} */
    const next = new Map();
    for (const { title } of measured) next.set(title, '');
    rows.forEach((row) => {
      if (row.length < 2) return;
      const tallest = Math.max(...row.map((entry) => entry.height ?? 0));
      row.forEach(({ title }) => next.set(title, `${Math.ceil(tallest)}px`));
    });
    next.forEach((value, title) => {
      if (title.style.minHeight !== value) title.style.minHeight = value;
    });
  });
}

let cardRowResizeTimer = 0;
window.addEventListener('resize', () => {
  clearTimeout(cardRowResizeTimer);
  cardRowResizeTimer = window.setTimeout(alignCardTitleRows, 150);
});
window.addEventListener('load', alignCardTitleRows);
document.fonts?.ready.then(alignCardTitleRows);
alignCardTitleRows();

// Filters, load more, search and lazy recommendation sections add cards after load.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement && (node.matches('product-card') || node.querySelector('product-card'))) {
        alignCardTitleRows();
        return;
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
